/**
 * SettingsPage — User settings with billing/subscription management.
 * Accessible from sidebar via /settings route.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { canAccessShopifyIntegration } from "@shared/shopify-access";
import {
  ArrowLeft,
  CreditCard,
  User,
  Shield,
  Sparkles,
  Globe,
  Sun,
  Moon,
  Monitor,
  Wallet,
  FileText,
  Coins,
  Cookie,
} from "lucide-react";
import { Link, useSearch } from "wouter";
import { Switch } from "@/components/ui/switch";
import {
  ONBOARDING_BAR_PREF_EVENT,
  isOnboardingBarHidden,
  setOnboardingBarHidden,
} from "@/lib/onboarding-bar-pref";
import { useQuery } from "@tanstack/react-query";
import BillingPanel from "@/components/billing-panel";
import InvoicesPanel from "@/components/invoices-panel";
import TokenUsagePanel from "@/components/token-usage-panel";
import { EcommerceCostsPanel } from "@/pages/ecommerce-costs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Field, FieldLabel } from "@/components/ui/field";
import { TwoFactorSetup } from "@/components/TwoFactorSetup";
import { useTheme } from "@/components/theme-provider";
import { showCookiePreferences } from "@/lib/consent/cookie-consent";

const SETTINGS_TABS = ["billing", "invoices", "usage", "account", "ecommerce"] as const;

type SettingsTab = (typeof SETTINGS_TABS)[number];

/** Jedno miejsce walidacji `?tab=` — wcześniej lista zakładek żyła w dwóch kopiach. */
function parseTab(search: string): SettingsTab | null {
  const requested = new URLSearchParams(search).get("tab");
  return SETTINGS_TABS.includes(requested as SettingsTab) ? (requested as SettingsTab) : null;
}

function useTabs(showEcommerce: boolean) {
  const { t } = useTranslation();
  const tabs: { id: SettingsTab; label: string; icon: JSX.Element }[] = [
    { id: "billing", label: t("settings.tabBilling"), icon: <CreditCard className="w-4 h-4" /> },
    { id: "invoices", label: t("settings.tabInvoices"), icon: <FileText className="w-4 h-4" /> },
    { id: "usage", label: t("settings.tabUsage"), icon: <Coins className="w-4 h-4" /> },
    { id: "account", label: t("settings.tabAccount"), icon: <User className="w-4 h-4" /> },
  ];
  if (showEcommerce) {
    tabs.push({ id: "ecommerce", label: "E-commerce", icon: <Wallet className="w-4 h-4" /> });
  }
  return tabs;
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    () => parseTab(window.location.search) ?? "billing"
  );
  // VP-256: `?tab=` czytane tylko przy montowaniu oznaczało, że skrót „Dokup tokeny"
  // (przycisk „+" przy liczniku) nic nie robił, gdy ustawienia były już otwarte na
  // innej zakładce. `useSearch` łapie zmianę samego query — `useLocation` by nie.
  const search = useSearch();
  useEffect(() => {
    const requested = parseTab(search);
    if (requested) setActiveTab(requested);
  }, [search]);

  // Zakładka e-commerce tylko dla uprawnionych do Shopify (super_admin / whitelista
  // testerów, VP-223). Wewnątrz zachowujemy dotychczasową logikę: super_admin widzi
  // zawsze, tester z whitelisty — gdy ma połączony sklep. Reszta nie odpytuje statusu.
  const canSeeShopify = canAccessShopifyIntegration(user);
  const isSuperAdmin = user?.role === "super_admin";
  const { data: shopifyStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["/api/integrations/shopify/status"],
    queryFn: () =>
      fetch("/api/integrations/shopify/status", { credentials: "include" }).then((r) => r.json()),
    staleTime: 60_000,
    enabled: canSeeShopify,
  });
  const showEcommerce = canSeeShopify && (isSuperAdmin || shopifyStatus?.connected === true);
  const TABS = useTabs(showEcommerce);

  return (
    <div className="h-full overflow-y-auto bg-[#030407]">
      {/* Top bar */}
      <div className="border-b border-white/[0.06] px-6 py-4">
        <div className="flex items-center gap-4 max-w-5xl mx-auto">
          <Link href="/chat/offer-builder">
            <button className="text-white/40 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          </Link>
          <div>
            <h1 className="text-white font-semibold text-lg">{t("settings.title")}</h1>
            <p className="text-white/40 text-xs">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar tabs */}
          <nav className="w-48 flex-shrink-0 space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-white/[0.08] text-white"
                    : "text-white/40 hover:text-white/60 hover:bg-white/[0.03]"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Tab content */}
          <div className="flex-1 min-w-0">
            {activeTab === "billing" && <BillingPanel />}
            {activeTab === "invoices" && <InvoicesPanel />}
            {activeTab === "usage" && <TokenUsagePanel />}
            {activeTab === "account" && <AccountSection />}
            {activeTab === "ecommerce" && showEcommerce && <EcommerceCostsPanel />}
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.newPassword.length < 6) {
      toast({
        title: t("settings.passwordTooShort"),
        description: t("settings.passwordTooShortDesc"),
        variant: "destructive",
      });
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast({ title: t("settings.passwordMismatch"), variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      await apiRequest("POST", "/api/auth/change-password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      toast({
        title: t("settings.passwordChanged"),
        description: t("settings.passwordChangedDesc"),
      });
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast({
        title: t("settings.tryAgain"),
        description: t("settings.tryAgainDesc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
        <h2 className="text-white font-semibold text-base mb-4">{t("settings.accountData")}</h2>
        <div className="space-y-4">
          <Field>
            <FieldLabel className="text-white/40">{t("auth.email")}</FieldLabel>
            <p className="text-white text-sm">{user?.email}</p>
          </Field>
          <Field>
            <FieldLabel className="text-white/40">{t("settings.role")}</FieldLabel>
            <p className="text-white text-sm">
              {user?.role === "super_admin"
                ? t("roles.superAdmin")
                : user?.role === "agency_owner"
                  ? t("roles.agencyOwner")
                  : user?.role === "employee"
                    ? t("roles.employee")
                    : t("roles.client")}
            </p>
          </Field>
          <Field>
            <FieldLabel className="text-white/40">{t("settings.createdAt")}</FieldLabel>
            <p className="text-white text-sm">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString(undefined) : "—"}
            </p>
          </Field>
        </div>
      </div>

      <OnboardingBarPreferenceSection />

      <CurrencyPreferenceSection />

      <ThemePreferenceSection />

      <CookiePreferencesSection />

      <TwoFactorSetup />

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-white/40" />
          <h2 className="text-white font-semibold text-base">{t("settings.changePassword")}</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field>
            <FieldLabel className="text-white/40">{t("settings.currentPassword")}</FieldLabel>
            <Input
              name="currentPassword"
              type="password"
              value={form.currentPassword}
              onChange={handleChange}
              placeholder={t("settings.currentPassword")}
              className="bg-white/[0.06] border-white/[0.1] text-white"
            />
          </Field>
          <Field>
            <FieldLabel className="text-white/40">{t("settings.newPassword")}</FieldLabel>
            <Input
              name="newPassword"
              type="password"
              value={form.newPassword}
              onChange={handleChange}
              placeholder={t("settings.newPasswordPlaceholder")}
              className="bg-white/[0.06] border-white/[0.1] text-white"
            />
          </Field>
          <Field>
            <FieldLabel className="text-white/40">{t("settings.repeatPassword")}</FieldLabel>
            <Input
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder={t("settings.repeatPassword")}
              className="bg-white/[0.06] border-white/[0.1] text-white"
            />
          </Field>
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
          >
            {isLoading ? t("settings.saving") : t("settings.saveButton")}
          </Button>
        </form>
      </div>
    </div>
  );
}

const THEME_OPTION_KEYS = [
  {
    value: "light" as const,
    labelKey: "settings.themeLight",
    descKey: "settings.themeLightDesc",
    icon: Sun,
  },
  {
    value: "dark" as const,
    labelKey: "settings.themeDark",
    descKey: "settings.themeDarkDesc",
    icon: Moon,
  },
  {
    value: "system" as const,
    labelKey: "settings.themeSystem",
    descKey: "settings.themeSystemDesc",
    icon: Monitor,
  },
];

// VP-312: user musi móc wycofać/zmienić zgodę w każdej chwili — to twarde wymaganie RODO,
// więc panel zgód ma stały punkt wejścia w ustawieniach, nie tylko przy pierwszej wizycie.
function CookiePreferencesSection() {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-border bg-card/50 p-6">
      <div className="flex items-center gap-2 mb-2">
        <Cookie className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-foreground font-semibold text-base">
          {t("cookies.settingsCard.title")}
        </h2>
      </div>
      <p className="text-muted-foreground text-xs mb-4">{t("cookies.settingsCard.description")}</p>
      <Button variant="outline" onClick={showCookiePreferences}>
        {t("cookies.settingsCard.button")}
      </Button>
    </div>
  );
}

function ThemePreferenceSection() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  return (
    <div className="rounded-xl border border-border bg-card/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sun className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-foreground font-semibold text-base">{t("settings.theme")}</h2>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {THEME_OPTION_KEYS.map((option) => {
          const Icon = option.icon;
          const isActive = theme === option.value;
          return (
            <button
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all ${
                isActive
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:border-primary/50 hover:bg-accent"
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm font-medium">{t(option.labelKey)}</span>
              <span className="text-xs text-muted-foreground text-center">{t(option.descKey)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CURRENCY_OPTION_KEYS: { value: string | null; labelKey: string; descKey: string }[] = [
  {
    value: null,
    labelKey: "settings.currencyDefaultLabel",
    descKey: "settings.currencyDefaultDesc",
  },
  { value: "PLN", labelKey: "settings.currencyPlnLabel", descKey: "settings.currencyPlnDesc" },
  { value: "USD", labelKey: "settings.currencyUsdLabel", descKey: "settings.currencyUsdDesc" },
  { value: "EUR", labelKey: "settings.currencyEurLabel", descKey: "settings.currencyEurDesc" },
];

function CurrencyPreferenceSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const currentPref = user?.preferredCurrency ?? null;

  const handleSelect = async (value: string | null) => {
    if (value === currentPref) return;
    setIsSaving(true);
    try {
      await apiRequest("PUT", "/api/user/currency-preference", { preferredCurrency: value });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: t("settings.saved"), description: t("settings.currencySavedDesc") });
    } catch (error) {
      toast({
        title: t("settings.tryAgain"),
        description: t("settings.tryAgainDesc"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-4 h-4 text-white/40" />
        <h2 className="text-white font-semibold text-base">{t("settings.currency")}</h2>
      </div>
      <p className="text-white/40 text-xs mb-4">{t("settings.currencyDesc")}</p>
      <div className="space-y-2">
        {CURRENCY_OPTION_KEYS.map((opt) => {
          const isActive = opt.value === currentPref;
          return (
            <button
              key={opt.value ?? "default"}
              onClick={() => handleSelect(opt.value)}
              disabled={isSaving}
              className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all ${
                isActive
                  ? "bg-white/[0.08] border border-white/[0.15]"
                  : "bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1]"
              }`}
            >
              <div
                className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isActive ? "border-red-500" : "border-white/20"
                }`}
              >
                {isActive && <div className="w-2 h-2 rounded-full bg-red-500" />}
              </div>
              <div>
                <span
                  className={`text-sm font-medium ${isActive ? "text-white" : "text-white/70"}`}
                >
                  {t(opt.labelKey)}
                </span>
                <p className="text-xs text-white/40 mt-0.5">{t(opt.descKey)}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OnboardingBarPreferenceSection() {
  const [hidden, setHidden] = useState<boolean>(() => isOnboardingBarHidden());
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sync = (): void => setHidden(isOnboardingBarHidden());
    window.addEventListener(ONBOARDING_BAR_PREF_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ONBOARDING_BAR_PREF_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("focus") === "onboardingBar") {
      setHighlight(true);
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setHighlight(false), 2400);
      return () => clearTimeout(t);
    }
  }, []);

  const enabled = !hidden;
  const handleToggle = (next: boolean): void => {
    setOnboardingBarHidden(!next);
  };

  return (
    <div
      ref={ref}
      className={`rounded-xl border bg-white/[0.02] p-6 transition-colors ${
        highlight
          ? "border-[#ef3e29]/60 shadow-[0_0_0_3px_rgba(239,62,41,0.15)]"
          : "border-white/[0.08]"
      }`}
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-white/40 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-white font-semibold text-base">
              Pasek konfiguracji Viral Partners
            </h2>
            <Switch checked={enabled} onCheckedChange={handleToggle} />
          </div>
          <p className="text-white/50 text-xs mt-1.5">
            Pokazuj pasek postępu konfiguracji konta na górze ekranu, dopóki wszystkie kroki nie
            zostaną ukończone. Możesz go w każdej chwili włączyć ponownie.
          </p>
        </div>
      </div>
    </div>
  );
}
